import { Link } from 'react-router-dom';
import { ChevronRight, Check } from 'lucide-react';
import { SEOHead, getServiceSchema, getBreadcrumbSchema } from './SEO';

const FULL_SERVICES = [
  {
    title: "Towing Services",
    desc: "Fast and reliable towing available anytime. Our tow trucks are ready to help with any vehicle size, from cars to larger personal trucks (non-semi). We offer quick response times and fair prices.",
    items: ["Car and Truck Towing", "Motorcycle Towing", "Forklift Hauling", "Light Duty Towing", "Flatbed Service"],
    img: "https://images.unsplash.com/photo-1686966933735-305bd8fe0a77?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHx0b3clMjB0cnVja3xlbnwwfHx8fDE3NjIyODEwOTF8MA&ixlib=rb-4.1.0&q=85",
    imgAlt: "A2B Towing professional towing services in Orangevale and Sacramento County",
  },
  {
    title: "Roadside Assistance",
    desc: "Stuck on the road? We offer quick roadside help including jump starts, tire changes, fuel delivery, and lockout services in Orangevale and nearby areas.",
    items: ["Jump Starts", "Tire Changes", "Fuel Delivery", "Lockout Service", "Battery Replacement"],
    img: "https://images.unsplash.com/photo-1543618208-03ccf87da0ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHw0fHxjYXIlMjBicmVha2Rvd258ZW58MHx8fHwxNzYyMjgxMTEwfDA&ixlib=rb-4.1.0&q=85",
    imgAlt: "A2B Towing roadside assistance in Orangevale and Sacramento County",
  },
  {
    title: "Hauling Services",
    desc: "Professional hauling services for cars, trucks, and equipment. Our skilled team uses the right tools and methods to move your vehicles safely.",
    items: ["Equipment Hauling", "Tool Box Moving", "Large Appliances Hauling", "Vehicle Transport", "Safe and Secure"],
    img: "https://images.unsplash.com/photo-1730514785075-b065c757b653?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHx0b3clMjB0cnVja3xlbnwwfHx8fDE3NjIyODEwOTF8MA&ixlib=rb-4.1.0&q=85",
    imgAlt: "A2B Towing professional hauling services in Sacramento County",
  },
  {
    title: "Load Transfer",
    desc: "Need to transfer your load from one vehicle to another? Our experienced team handles load transfers with care and efficiency, ensuring your cargo reaches its destination safely.",
    items: ["Safe Load Transfer", "Cargo Handling", "Equipment Operation", "Professional Team", "Timely Service"],
    img: "https://images.pexels.com/photos/943930/pexels-photo-943930.jpeg",
    imgAlt: "A2B Towing load transfer services in Orangevale",
  },
];

function ServicesPage() {
  const schema = [
    getBreadcrumbSchema([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }]),
    getServiceSchema("Towing & Roadside Assistance Services", "Complete towing and roadside assistance solutions in Orangevale and Sacramento County")
  ];

  return (
    <div className="min-h-screen bg-black pt-16" data-testid="services-page">
      <SEOHead
        title="Towing & Roadside Assistance Services in Orangevale CA"
        description="Towing & roadside assistance: flatbed towing, jump starts, tire changes, fuel delivery, lockout service. Transparent pricing. Call (916) 234-3923"
        canonical="/services"
        schema={schema}
      />

      <section className="pt-16 pb-20 bg-zinc-950">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <div className="inline-block mb-4 px-4 py-2 bg-red-600/10 border border-red-600/30 rounded-full">
              <span className="text-red-600 text-sm font-semibold">Our Services</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6" data-testid="services-heading">Complete Towing & Roadside Assistance Solutions</h1>
            <p className="text-xl text-gray-400 mb-8">From emergency towing to roadside help, we provide comprehensive solutions for all your vehicle needs in Orangevale and surrounding areas.</p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 space-y-20">
          {FULL_SERVICES.map((svc, i) => (
            <div key={svc.title} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${i % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}>
              <div className={i % 2 !== 0 ? 'lg:order-2' : ''}>
                <img alt={svc.imgAlt} className="rounded-lg shadow-2xl w-full h-[400px] object-cover" src={svc.img} loading="lazy" />
              </div>
              <div className={i % 2 !== 0 ? 'lg:order-1' : ''}>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{svc.title}</h2>
                <p className="text-gray-400 text-lg mb-6 leading-relaxed">{svc.desc}</p>
                <div className="space-y-3 mb-8">
                  {svc.items.map(item => (
                    <div key={item} className="flex items-center space-x-3">
                      <div className="w-5 h-5 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-gray-300">{item}</span>
                    </div>
                  ))}
                </div>
                <Link to="/quote">
                  <button className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 text-sm font-semibold rounded-md transition-all inline-flex items-center gap-2">
                    Request This Service <ChevronRight className="w-4 h-4" />
                  </button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-zinc-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-block mb-4 px-4 py-2 bg-red-600/10 border border-red-600/30 rounded-full">
              <span className="text-red-600 text-sm font-semibold">Transparent Pricing</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Fair & Competitive Rates</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">No hidden fees. Upfront pricing you can trust.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8" data-testid="pricing-towing">
              <h3 className="text-2xl font-bold text-white mb-4">Towing Services</h3>
              <div className="text-4xl font-bold text-red-600 mb-2">$150</div>
              <p className="text-gray-400 mb-4">Hook Rate</p>
              <div className="pt-4 border-t border-zinc-800">
                <p className="text-gray-400 text-sm">Enroute: $4/mile</p>
                <p className="text-gray-400 text-sm">Loaded: $5/mile</p>
              </div>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8" data-testid="pricing-roadside">
              <h3 className="text-2xl font-bold text-white mb-4">Roadside Services</h3>
              <div className="text-4xl font-bold text-red-600 mb-2">$75</div>
              <p className="text-gray-400 mb-4">Flat Rate</p>
              <div className="pt-4 border-t border-zinc-800 space-y-2">
                {["Tire Changes", "Battery Jump Starts", "Fuel Delivery", "Lockout Services"].map(s => (
                  <div key={s} className="flex items-center gap-2 text-gray-400 text-sm">
                    <Check className="w-4 h-4 text-red-600" /> {s}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="text-center mt-12">
            <Link to="/quote">
              <button className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 text-lg font-semibold rounded-md transition-all inline-flex items-center gap-2">
                Get Your Free Quote <ChevronRight className="w-5 h-5" />
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ServicesPage;
